import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getConfig } from '../config';
import { CognitoUserPool, AuthenticationDetails, CognitoUser } from 'amazon-cognito-identity-js';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cognitoConfig, setCognitoConfig] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const loadConfigAndAuth = async () => {
            try {
                const config = await getConfig();

                setCognitoConfig({
                    region: config.cognitoRegion,
                    userPoolId: config.cognitoUserPoolId,
                    clientId: config.cognitoAppClientId
                });

                const token = localStorage.getItem('token');
                if (token) {
                    try {
                        const tokenData = parseJwt(token);

                        setCurrentUser({
                            username: tokenData.username || tokenData.email || 'User',
                            email: tokenData.email || '',
                            roles: tokenData['cognito:groups'] || [],
                            isSuperUser: (tokenData['cognito:groups'] || []).includes('SUPER_USER')
                        });
                    } catch (error) {
                        console.error('Invalid token:', error);
                        localStorage.removeItem('token');
                    }
                }
            } catch (error) {
                console.error('Error initializing auth:', error);
                setError('Failed to initialize authentication');
            } finally {
                setIsLoading(false);
            }
        };

        loadConfigAndAuth();
    }, []);

    const parseJwt = (token) => {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Error parsing JWT:', error);
            return {};
        }
    };

    const login = async (email, password) => {
        try {
            setIsLoading(true);
            setError(null);

            if (!cognitoConfig) {
                throw new Error('Authentication configuration not loaded');
            }

            console.log('Attempting Cognito authentication with:', email);

            const userPool = new CognitoUserPool({
                UserPoolId: cognitoConfig.userPoolId,
                ClientId: cognitoConfig.clientId
            });

            const cognitoUser = new CognitoUser({
                Username: email,
                Pool: userPool
            });

            const authDetails = new AuthenticationDetails({
                Username: email,
                Password: password
            });

            return new Promise((resolve, reject) => {
                cognitoUser.authenticateUser(authDetails, {
                    onSuccess: (session) => {
                        const token = session.getIdToken().getJwtToken();
                        localStorage.setItem('token', token);

                        const userInfo = parseJwt(token);

                        const user = {
                            username: userInfo.username || userInfo.email || 'User',
                            email: userInfo.email || '',
                            roles: userInfo['cognito:groups'] || [],
                            isSuperUser: (userInfo['cognito:groups'] || []).includes('SUPER_USER')
                        };

                        setCurrentUser(user);
                        console.log('Login successful:', user);

                        setIsLoading(false);
                        resolve(true);
                    },
                    onFailure: (err) => {
                        console.error('Cognito authentication error:', err);
                        setError(err.message || 'Authentication failed');
                        setIsLoading(false);
                        resolve(false);
                    }
                });
            });
        } catch (error) {
            console.error('Login error:', error);
            setError(error.message);
            setIsLoading(false);
            return false;
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setCurrentUser(null);
        navigate('/login');
    };

    const hasRole = (role) => {
        return currentUser?.roles?.includes(role) || false;
    };

    const value = {
        currentUser,
        token: localStorage.getItem('token'),
        isLoading,
        error,
        login,
        logout,
        hasRole,
        isSuperUser: currentUser?.isSuperUser || false
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === null) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;